import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { EnhancedSelectOption } from '@/components/form/types';
import { getFamilyLabel } from '@/types/changeRequest';
import { ChildDataUpdateRequestForm } from './ChildDataUpdateRequestForm';
import { CareDayRequestForm } from '@/components/change-requests/CareDayRequestForm';
import { PauseRequestForm } from '@/components/change-requests/PauseRequestForm';

/**
 * Contract-level context that unlocks the care_day / pause create families. When
 * present, the dialog renders a family selector (children / care_day / pause);
 * when absent, only the children form is shown (child-page entry points, unchanged).
 */
export interface CreateChangeRequestContractContext {
  contractGroupId: string;
  versionId?: string;
  activeFrom?: string;
  activeTo?: string;
  childId?: string;
  childName?: string;
}

interface CreateChangeRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Targets to pre-fill (e.g. the current child on an entry-point page). */
  initialChildIds?: string[];
  /** Display labels for the prefilled targets, so the picker shows the child's name. */
  initialChildOptions?: EnhancedSelectOption[];
  /** Lock the target picker (single-child entry-point launch). */
  lockTarget?: boolean;
  /** When provided, unlocks the family selector + the care_day / pause forms. */
  contractContext?: CreateChangeRequestContractContext;
}

type ChangeRequestFamily = 'children' | 'care_day' | 'pause';

/**
 * Shared host for the change-request create forms. Every create entry point
 * (children list, child detail, contract editor) opens the form through this
 * single wrapper. From child pages it renders the canonical
 * {@link ChildDataUpdateRequestForm} directly. From the Contract editor (via
 * `contractContext`) it renders a family selector that dispatches to the
 * children / care_day / pause forms.
 */
export function CreateChangeRequestDialog({
  open,
  onOpenChange,
  initialChildIds,
  initialChildOptions,
  lockTarget = false,
  contractContext,
}: CreateChangeRequestDialogProps) {
  const { i18n } = useLingui();
  // Default to care_day when contract context is present — that is the primary
  // reason an admin opens this dialog from the Contract editor.
  const [family, setFamily] = useState<ChangeRequestFamily>('care_day');

  // Reset the family selection each time the dialog opens so a prior session's
  // choice does not persist and reopen with a stale value instead of the default.
  useEffect(() => {
    if (open) {
      setFamily('care_day');
    }
  }, [open]);

  const close = () => onOpenChange(false);

  const familyOptions: Array<{ value: ChangeRequestFamily; label: string }> = [
    { value: 'children', label: getFamilyLabel(i18n, 'children') },
    { value: 'care_day', label: getFamilyLabel(i18n, 'care_day') },
    { value: 'pause', label: getFamilyLabel(i18n, 'pause') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {contractContext
              ? i18n._({ id: 'changeRequest.dialog.contractTitle', message: 'Request a change' })
              : i18n._({ id: 'changeRequest.dialog.title', message: 'Request a child data change' })}
          </DialogTitle>
          <DialogDescription>
            {contractContext
              ? i18n._({
                  id: 'changeRequest.dialog.contractDescription',
                  message:
                    'Capture a contract change as a request that flows through the approval workflow instead of editing the record directly.',
                })
              : i18n._({
                  id: 'changeRequest.dialog.description',
                  message:
                    'Capture a child data change as a request that flows through the approval workflow instead of editing the record directly.',
                })}
          </DialogDescription>
        </DialogHeader>

        {contractContext && (
          <div className="space-y-2">
            <Label htmlFor="change-request-family">
              <Trans id="changeRequest.field.family">Change category</Trans>
            </Label>
            <Select value={family} onValueChange={(v) => setFamily(v as ChangeRequestFamily)}>
              <SelectTrigger
                id="change-request-family"
                aria-label={i18n._({ id: 'changeRequest.field.family', message: 'Change category' })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {familyOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!contractContext || family === 'children' ? (
          <ChildDataUpdateRequestForm
            initialChildIds={initialChildIds}
            initialChildOptions={initialChildOptions}
            lockTarget={lockTarget}
            onSuccess={close}
            onCancel={close}
          />
        ) : family === 'care_day' ? (
          <CareDayRequestForm
            contractGroupId={contractContext.contractGroupId}
            versionId={contractContext.versionId}
            activeRangeFrom={contractContext.activeFrom}
            activeRangeTo={contractContext.activeTo}
            onSuccess={close}
            onCancel={close}
          />
        ) : (
          <PauseRequestForm
            contractGroupId={contractContext.contractGroupId}
            versionId={contractContext.versionId}
            activeRangeFrom={contractContext.activeFrom}
            activeRangeTo={contractContext.activeTo}
            onSuccess={close}
            onCancel={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
